import { useTranslation } from "react-i18next";

import type { CcParityStatus } from "@/api/code-companion";
import { useCcParity } from "@/hooks/useCodeCompanion";

/**
 * Paridade: o código novo faz o mesmo que o antigo? (S-064 onda 5)
 *
 * O caso é derivado do objeto **legado**, vira teste ABAP Unit e roda no alvo. É a única
 * coisa no produto que responde essa pergunta — ativar e passar no ATC prova que o
 * código é válido, não que ele preserva o comportamento.
 *
 * **Zero casos não é verde.** Ausência de prova não é prova, e é exatamente o falso
 * positivo registrado na CC.28: um stub ativa, o ATC fica limpo, e sem caso derivado o
 * objeto fecharia verde sem nada ter sido verificado. O veredito vem do servidor.
 *
 * **`error` conta como divergência.** Caso que nem rodou não prova paridade.
 */

/** O resumo em uma palavra, e a cor. Deriva do que o servidor já decidiu. */
export function parityHeadline(
  p: CcParityStatus,
): { key: "match" | "drift" | "pending" | "none"; tone: string } {
  if (!p.cases.length) return { key: "none", tone: "#b45309" };
  if (p.has_drift) return { key: "drift", tone: "#b02a31" };
  if (p.pending) return { key: "pending", tone: "#6b7280" };
  if (p.all_match) return { key: "match", tone: "#2b8f57" };
  return { key: "pending", tone: "#6b7280" };
}

const VERDICT_TONE: Record<string, string> = {
  match: "#2b8f57",
  drift: "#b02a31",
  error: "#b02a31",
  pending: "#6b7280",
  // Retirado do escopo com autor e motivo: não é prova e não é falha.
  dropped: "#8a6100",
};

export default function ParityPanel({
  projectId,
  packageId,
}: {
  projectId: string;
  packageId: number;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useCcParity(projectId, packageId);

  if (isLoading || !data) return null;

  const resumo = parityHeadline(data);
  const SUMMARY_TEXT: Record<string, string> = {
    // "comportamento preservado" promete comparação entre ECC e GROW. O que houve foi
    // outra coisa: o alvo satisfez uma expectativa que um modelo derivou LENDO o legado,
    // e o legado nunca foi executado. A frase tem de caber no que o sistema sabe.
    match: t("target matches the expectation derived from the legacy source"),
    drift: t("behaviour changed in at least one case"),
    pending: t("cases derived, not run yet"),
    // Dito com todas as letras: é o falso positivo que a paridade existe para evitar.
    none: t("no parity case was derived, so nothing was verified"),
  };

  return (
    <div
      data-testid="parity-panel"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Parity with the legacy")}
        </span>
        <span
          data-testid="parity-headline"
          className="text-[11px] font-medium"
          style={{ color: resumo.tone }}
        >
          {SUMMARY_TEXT[resumo.key]}
        </span>
      </div>

      {data.cases.length > 0 && (
        <ul className="divide-y divide-border">
          {data.cases.map((c) => (
            <li key={c.case_id} className="flex items-baseline gap-2 px-3 py-1.5 text-[11px]">
              <span
                className="font-mono text-[10px] font-semibold uppercase"
                style={{ color: VERDICT_TONE[c.verdict] ?? "#6b7280" }}
              >
                {c.verdict}
              </span>
              {c.object_name && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {c.object_name}
                </span>
              )}
              <span className="break-words">{c.case_id}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
